import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { useCriarOrdemServico, useAtualizarOrdemServico } from '@/hooks/useOrdensServico';
import { useCriarItensOS, useItensOS, useServicosAtivos } from '@/hooks/useServicos';
import { usePrestadores } from '@/hooks/usePrestadores';
import { useProfilesComRoles } from '@/hooks/useProfiles';
import { filtrarPrestadoresAtribuiveis } from '@/lib/prestadores-atribuiveis';
import { useClientes } from '@/hooks/useClientes';
import { useAuthContext } from '@/contexts/AuthContext';
import { SeletorServicos } from './SeletorServicos';
import type { 
  OrdemServico, 
  EnderecoOS,
} from '@/types/ordemServico';
import type { ItemSelecionado } from '@/types/servico';
import { calcularTotalItens, formatarValor } from '@/types/servico';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { useCriarRecorrencia } from '@/hooks/useRecorrencias';
import { isPaidPlan } from '@/lib/planos';
import { validarServicosOS } from '@/lib/os-servicos';
import { construirItensOS, construirServicosLegacy } from '@/lib/os-itens-builder';
import type { FrequenciaRecorrencia } from '@/types/recorrencia';
import { Loader2, Package, Repeat } from 'lucide-react';

// Tipo completo para cliente pré-selecionado com dados de endereço
interface ClientePreSelecionado {
  id: string;
  nome?: string | null;
  telefone?: string;
  endereco_rua?: string | null;
  endereco_numero?: string | null;
  endereco_cidade?: string | null;
  endereco_codigo_postal?: string | null;
  endereco_regiao?: string | null;
  endereco_pais?: string | null;
  endereco_complemento?: string | null;
}

interface OrdemServicoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  osParaEditar?: OrdemServico;
  clientePreSelecionado?: string | ClientePreSelecionado;
}

export const OrdemServicoFormDialog = React.forwardRef<
  HTMLDivElement,
  OrdemServicoFormDialogProps
>(function OrdemServicoFormDialog(
  { 
    open, 
    onOpenChange,
    osParaEditar,
    clientePreSelecionado,
  },
  _ref
) {
  const { t } = useTranslation();
  const { profile, organization } = useAuthContext();
  const criarOS = useCriarOrdemServico();
  const atualizarOS = useAtualizarOrdemServico();
  const criarItensOS = useCriarItensOS();
  const criarRecorrencia = useCriarRecorrencia();
  // Atribuir um prestador na criação/edição da OS é uma ação de planeamento do
  // admin/atendente — lista TODOS os prestadores de membros ativos
  // (`organization_members.ativo = true`; membros desativados ficam de fora).
  const { data: prestadoresRaw, isLoading: loadingPrestadores } = usePrestadores();
  const { data: prestadoresAtivos } = useProfilesComRoles('prestador');
  const prestadores = useMemo(
    () =>
      filtrarPrestadoresAtribuiveis(
        prestadoresRaw ?? [],
        new Set((prestadoresAtivos ?? []).map((p) => p.id)),
      ),
    [prestadoresRaw, prestadoresAtivos],
  );
  const { data: clientes, isLoading: loadingClientes } = useClientes();
  const { data: servicosAtivos } = useServicosAtivos();
  
  // Buscar itens existentes quando editando uma OS
  const { data: itensExistentes, isLoading: loadingItens } = useItensOS(osParaEditar?.id);

  const [clienteId, setClienteId] = useState<string>('');
  const [prestadorId, setPrestadorId] = useState<string>('');
  const [itensSelecionados, setItensSelecionados] = useState<ItemSelecionado[]>([]);
  const [dataAgendada, setDataAgendada] = useState<string>('');
  const [observacoes, setObservacoes] = useState<string>('');
  
  // Endereço
  const [rua, setRua] = useState<string>('');
  const [numero, setNumero] = useState<string>('');
  const [complemento, setComplemento] = useState<string>('');
  const [codigoPostal, setCodigoPostal] = useState<string>('');
  const [cidade, setCidade] = useState<string>('');
  const [regiao, setRegiao] = useState<string>('');
  const [pais, setPais] = useState<string>('');
  const [dadosCarregados, setDadosCarregados] = useState<boolean>(false);

  // Recorrência (apenas ao criar uma OS nova)
  const [repetir, setRepetir] = useState<boolean>(false);
  const [frequencia, setFrequencia] = useState<FrequenciaRecorrencia>('semanal');
  const planoPago = isPaidPlan(organization?.plano);

  // Verificar se cliente está pré-selecionado e bloqueado
  const clienteBloqueado = typeof clientePreSelecionado === 'object' && clientePreSelecionado?.id;

  // Calcular total automaticamente
  const valorTotal = calcularTotalItens(itensSelecionados);

  // Função para preencher dados do cliente selecionado
  const preencherDadosCliente = (clienteIdSelecionado: string) => {
    const clienteEncontrado = clientes?.find(c => c.id === clienteIdSelecionado);
    
    if (clienteEncontrado) {
      // Preencher endereço com dados do cliente
      setRua(clienteEncontrado.endereco_rua || '');
      setNumero(clienteEncontrado.endereco_numero || '');
      setComplemento(clienteEncontrado.endereco_complemento || '');
      setCodigoPostal(clienteEncontrado.endereco_codigo_postal || '');
      setCidade(clienteEncontrado.endereco_cidade || '');
      setRegiao(clienteEncontrado.endereco_regiao || '');
      setPais(clienteEncontrado.endereco_pais || '');
      
      // Indicar que dados foram carregados do cliente
      const temEndereco = clienteEncontrado.endereco_rua || clienteEncontrado.endereco_cidade;
      setDadosCarregados(!!temEndereco);
    }
  };

  // Handler para mudança de cliente no Select
  const handleClienteChange = (novoClienteId: string) => {
    setClienteId(novoClienteId);
    preencherDadosCliente(novoClienteId);
  };

  // Carregar itens existentes quando editando uma OS
  useEffect(() => {
    if (osParaEditar && itensExistentes && itensExistentes.length > 0 && servicosAtivos) {
      // Converter itens da OS para ItemSelecionado
      const itensConvertidos: ItemSelecionado[] = itensExistentes.map(item => {
        // Tentar encontrar o serviço no catálogo atual
        const servicoCatalogo = servicosAtivos.find(s => s.id === item.servico_id);
        
        return {
          servico: {
            id: item.servico_id,
            nome: item.servico_nome,
            descricao: servicoCatalogo?.descricao || '',
            tipo_preco: servicoCatalogo?.tipo_preco || 'fixo',
            valor_base: item.valor_unitario,
            unidade: servicoCatalogo?.unidade || 'serviço',
            ativo: true,
            criado_em: '',
            atualizado_em: '',
          },
          quantidade: item.quantidade,
        };
      });
      
      setItensSelecionados(itensConvertidos);
    }
  }, [osParaEditar, itensExistentes, servicosAtivos]);

  // Inicializa o formulário UMA vez por abertura. Sem este guard, qualquer
  // re-render do componente-pai (refetch/realtime do Painel) que passe um novo
  // objeto `clientePreSelecionado` (recriado inline em ClienteProfile/
  // ClienteDetalhesModal/ClientePotential) re-disparava este efeito e chamava
  // resetForm(), apagando o serviço/endereço que o utilizador estava a preencher.
  const formInicializadoRef = useRef(false);

  useEffect(() => {
    if (!open) {
      formInicializadoRef.current = false;
      return;
    }
    if (formInicializadoRef.current) return;
    formInicializadoRef.current = true;

    if (osParaEditar) {
      setClienteId(osParaEditar.cliente_id || '');
      setPrestadorId(osParaEditar.prestador_id || '');
      // Itens são carregados pelo useEffect acima
      setDataAgendada(osParaEditar.data_agendada?.slice(0, 16) || '');
      setObservacoes(osParaEditar.observacoes || '');
      setRua(osParaEditar.endereco?.rua || '');
      setNumero(osParaEditar.endereco?.numero || '');
      setComplemento(osParaEditar.endereco?.complemento || '');
      setCodigoPostal(osParaEditar.endereco?.codigo_postal || '');
      setCidade(osParaEditar.endereco?.cidade || '');
      setRegiao(osParaEditar.endereco?.regiao || '');
      setPais(osParaEditar.endereco?.pais || '');
    } else {
      resetForm();
      // Pré-preencher com cliente selecionado
      if (typeof clientePreSelecionado === 'string') {
        setClienteId(clientePreSelecionado);
      } else if (clientePreSelecionado && typeof clientePreSelecionado === 'object') {
        setClienteId(clientePreSelecionado.id);
        // Pré-preencher endereço do cliente
        setRua(clientePreSelecionado.endereco_rua || '');
        setNumero(clientePreSelecionado.endereco_numero || '');
        setComplemento(clientePreSelecionado.endereco_complemento || '');
        setCodigoPostal(clientePreSelecionado.endereco_codigo_postal || '');
        setCidade(clientePreSelecionado.endereco_cidade || '');
        setRegiao(clientePreSelecionado.endereco_regiao || '');
        setPais(clientePreSelecionado.endereco_pais || '');
      }
    }
  }, [osParaEditar, clientePreSelecionado, open]);

  const resetForm = () => {
    setClienteId('');
    setPrestadorId('');
    setItensSelecionados([]);
    setDataAgendada('');
    setObservacoes('');
    setRua('');
    setNumero('');
    setComplemento('');
    setCodigoPostal('');
    setCidade('');
    setRegiao('');
    setPais('');
    setDadosCarregados(false);
    setRepetir(false);
    setFrequencia('semanal');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!dataAgendada) {
      toast.error(t('orders.form.dateRequired'));
      return;
    }

    const validacaoServicos = validarServicosOS(itensSelecionados);
    if (!validacaoServicos.ok) {
      toast.error(t('orders.form.singleHourlyService'));
      return;
    }

    const endereco: EnderecoOS = {
      rua: rua || undefined,
      numero: numero || undefined,
      complemento: complemento || undefined,
      codigo_postal: codigoPostal || undefined,
      cidade: cidade || undefined,
      pais: pais || undefined,
    };

    // Converter itens selecionados para o formato de serviços da OS (compatibilidade)
    const servicosLegacy = construirServicosLegacy(itensSelecionados);

    const dados = {
      cliente_id: clienteId || undefined,
      // `null` (não `undefined`) para que escolher "Não atribuído" REMOVA mesmo o
      // prestador no update (atualizarOrdemServico ignora campos `undefined`).
      prestador_id: prestadorId || null,
      responsavel_id: profile?.id,
      servicos: servicosLegacy,
      data_agendada: dataAgendada || undefined,
      endereco,
      valor_total: valorTotal,
      observacoes,
    };

    let osId: string;

    if (osParaEditar) {
      await atualizarOS.mutateAsync({ id: osParaEditar.id, dados });
      osId = osParaEditar.id;
    } else {
      const novaOS = await criarOS.mutateAsync(dados);
      osId = novaOS.id;
    }

    // Salvar itens detalhados na tabela ordem_servico_itens
    if (itensSelecionados.length > 0) {
      await criarItensOS.mutateAsync({
        ordemServicoId: osId,
        itens: construirItensOS(itensSelecionados),
      });
    }

    // Criar a recorrência (a OS acabada de criar é a 1ª ocorrência).
    if (!osParaEditar && repetir) {
      await criarRecorrencia.mutateAsync({
        cliente_id: clienteId,
        prestador_id: prestadorId || null,
        responsavel_id: profile?.id ?? null,
        frequencia,
        data_agendada: dataAgendada,
        servicos: servicosLegacy,
        itens: construirItensOS(itensSelecionados),
        endereco,
        valor_total: valorTotal,
        observacoes,
        duracao_estimada_minutos: null,
        primeira_os_id: osId,
      });
    }

    onOpenChange(false);
    resetForm();
  };

  const isPending = criarOS.isPending || atualizarOS.isPending || criarItensOS.isPending || criarRecorrencia.isPending || loadingItens;

  // Nome do cliente pré-selecionado para exibição
  const nomeClientePreSelecionado = typeof clientePreSelecionado === 'object' 
    ? clientePreSelecionado?.nome || clientePreSelecionado?.telefone 
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `!overflow-y-auto`: o Radix injeta `overflow: visible` inline no Content
          (modal alto fica sem scroll e o conteúdo vaza para fora). O `!important`
          afirma o nosso scroll por cima do inline. Seguro: os Select portam os
          dropdowns para fora do modal, logo não há recorte. */}
      <DialogContent className="max-w-2xl max-h-[90vh] !overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {osParaEditar ? t('orders.form.editTitle') : t('orders.form.title')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cliente */}
          <div className="space-y-2">
            <Label>{t('orders.form.client')}</Label>
            {clienteBloqueado ? (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <span className="font-medium">{nomeClientePreSelecionado}</span>
                <span className="text-muted-foreground ml-2">{t('orders.preSelected')}</span>
              </div>
            ) : (
              <Select value={clienteId} onValueChange={handleClienteChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('orders.form.selectClient')} />
                </SelectTrigger>
                <SelectContent>
                  {loadingClientes ? (
                    <div className="p-2 text-center text-sm text-muted-foreground">
                      {t('common.loading')}
                    </div>
                  ) : (
                    <>
                      {clientes?.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.nome || c.telefone}
                        </SelectItem>
                      ))}
                      {/* `useClientes` exclui arquivados; se a OS em edição tem um
                          cliente arquivado, ele não aparece na lista e o Select
                          ficaria vazio. Incluí-lo aqui para o mostrar/preservar. */}
                      {osParaEditar?.cliente?.id &&
                        !clientes?.some(c => c.id === osParaEditar.cliente!.id) && (
                          <SelectItem
                            key={osParaEditar.cliente.id}
                            value={osParaEditar.cliente.id}
                          >
                            {osParaEditar.cliente.nome || osParaEditar.cliente.telefone}
                          </SelectItem>
                        )}
                    </>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Prestador */}
          <div className="space-y-2">
            <Label>{t('orders.form.provider')}</Label>
            <Select value={prestadorId || '_none_'} onValueChange={(v) => setPrestadorId(v === '_none_' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('orders.form.selectProvider')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">{t('orders.form.notAssigned')}</SelectItem>
                {loadingPrestadores ? (
                  <div className="p-2 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </div>
                ) : (
                  prestadores?.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.profile?.nome || t('common.noName')}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Serviços - Novo Seletor */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              {t('orders.form.services')}
            </Label>
            <SeletorServicos
              value={itensSelecionados}
              onChange={setItensSelecionados}
            />
          </div>

          {/* Data e Hora + Valor Total */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                {t('orders.form.scheduledDate')} <span className="text-destructive">*</span>
              </Label>
              <Input
                type="datetime-local"
                value={dataAgendada}
                onChange={e => setDataAgendada(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('orders.form.totalValue')}</Label>
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg text-center">
                <span className="text-xl font-bold text-primary">
                  {formatarValor(valorTotal)}
                </span>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('orders.calculatedAutomatically')}
                </p>
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div className="space-y-4">
            <Label className="text-base font-semibold flex items-center gap-2">
              {t('orders.form.serviceAddress')}
              {dadosCarregados && (
                <span className="text-xs font-normal bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                  {t('orders.loadedFromClient')}
                </span>
              )}
            </Label>
            
            {/* Linha 1: Rua + Número */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>{t('orders.form.street')}</Label>
                <Input
                  value={rua}
                  onChange={e => { setRua(e.target.value); setDadosCarregados(false); }}
                  placeholder={t('orders.form.streetPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orders.form.number')}</Label>
                <Input
                  value={numero}
                  onChange={e => { setNumero(e.target.value); setDadosCarregados(false); }}
                  placeholder={t('orders.form.numberPlaceholder')}
                />
              </div>
            </div>

            {/* Linha 2: Complemento */}
            <div className="space-y-2">
              <Label>{t('orders.form.complement')}</Label>
              <Input
                value={complemento}
                onChange={e => { setComplemento(e.target.value); setDadosCarregados(false); }}
                placeholder={t('orders.form.complementPlaceholder')}
              />
            </div>

            {/* Linha 3: Código Postal + Cidade */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('orders.form.postalCode')}</Label>
                <Input
                  value={codigoPostal}
                  onChange={e => { setCodigoPostal(e.target.value); setDadosCarregados(false); }}
                  placeholder={t('orders.form.postalCodePlaceholder')}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>{t('orders.form.city')}</Label>
                <Input
                  value={cidade}
                  onChange={e => { setCidade(e.target.value); setDadosCarregados(false); }}
                  placeholder={t('orders.form.cityPlaceholder')}
                />
              </div>
            </div>

            {/* Linha 4: Região + País */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('orders.form.region')}</Label>
                <Input
                  value={regiao}
                  onChange={e => { setRegiao(e.target.value); setDadosCarregados(false); }}
                  placeholder={t('orders.form.regionPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orders.form.country')}</Label>
                <Input
                  value={pais}
                  onChange={e => { setPais(e.target.value); setDadosCarregados(false); }}
                  placeholder={organization?.pais ?? t('orders.form.countryPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Recorrência — apenas ao criar uma OS nova */}
          {!osParaEditar && (
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Repeat className="h-4 w-4" />
                  {t('recurrences.form.repeat')}
                </Label>
                <Switch
                  checked={repetir}
                  onCheckedChange={setRepetir}
                  disabled={!planoPago}
                />
              </div>

              {!planoPago && (
                <p className="text-xs text-muted-foreground">
                  {t('recurrences.form.lockedHint')}
                </p>
              )}

              {planoPago && repetir && (
                <div className="space-y-2">
                  <Label>{t('recurrences.form.frequencyLabel')}</Label>
                  <Select
                    value={frequencia}
                    onValueChange={(v) => setFrequencia(v as FrequenciaRecorrencia)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="semanal">{t('recurrences.frequency.weekly')}</SelectItem>
                      <SelectItem value="quinzenal">{t('recurrences.frequency.biweekly')}</SelectItem>
                      <SelectItem value="mensal">{t('recurrences.frequency.monthly')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('recurrences.form.repeatHint')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Observações */}
          <div className="space-y-2">
            <Label>{t('orders.form.notes')}</Label>
            <Textarea
              value={observacoes}
              onChange={e => setObservacoes(e.target.value)}
              placeholder={t('orders.form.notesPlaceholder')}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {osParaEditar ? t('common.save') : t('orders.createOrder')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

OrdemServicoFormDialog.displayName = 'OrdemServicoFormDialog';
